import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomer } from "@/context/CustomerAuthContext";
import capi from "@/lib/customerApi";
import { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, MockOtpNote } from "@/pages/customer/AuthShell";

const MOBILE_RX = /^[6-9]\d{9}$/;

export default function SignUp() {
  const { register } = useCustomer();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", mobile: "", password: "", confirm_password: "" });
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [mockCode, setMockCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const validate = () => {
    if (!f.name.trim()) return "Enter your full name";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return "Enter a valid email address";
    if (!MOBILE_RX.test(f.mobile)) return "Enter a valid 10-digit mobile number";
    if (f.password.length < 8) return "Password must be at least 8 characters";
    if (f.password !== f.confirm_password) return "Passwords do not match";
    return null;
  };

  const sendOtp = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data } = await capi.post("/auth/otp/send", { mobile: f.mobile, purpose: "signup" });
      setOtpSent(true);
      setMockCode(data.mock_otp || "");
      setCooldown(30);
      toast.success(`OTP sent to +91 ${f.mobile}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!otpSent) return sendOtp();
    if (otp.trim().length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      await register({ ...f, otp: otp.trim() });
      toast.success("Account created");
      nav("/my-account", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell eyebrow="Create account" title="Sign up" subtitle="Track all your bookings in one place." testid="signup-page">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Field id="su-name" label="Full Name" value={f.name} onChange={set("name")} disabled={otpSent} autoComplete="name" />
        <Field id="su-email" label="Email Address" type="email" value={f.email} onChange={set("email")} disabled={otpSent} autoComplete="email" />
        <div className="space-y-2">
          <Label htmlFor="su-mobile">Mobile Number</Label>
          <div className="flex">
            <span className="h-10 px-3 grid place-items-center border border-r-0 border-input bg-muted text-sm font-mono-num">+91</span>
            <Input id="su-mobile" inputMode="numeric" maxLength={10} value={f.mobile} disabled={otpSent}
              onChange={(e) => setF((s) => ({ ...s, mobile: e.target.value.replace(/\D/g, "") }))}
              className="rounded-none font-mono-num" data-testid="su-mobile" autoComplete="tel-national" />
          </div>
        </div>
        <Field id="su-password" label="Password" type="password" value={f.password} onChange={set("password")} disabled={otpSent} autoComplete="new-password" hint="At least 8 characters" />
        <Field id="su-confirm" label="Confirm Password" type="password" value={f.confirm_password} onChange={set("confirm_password")} disabled={otpSent} autoComplete="new-password" />

        {otpSent && (
          <div className="space-y-3 border-t border-border pt-4" data-testid="su-otp-block">
            <MockOtpNote code={mockCode} />
            <Label htmlFor="su-otp" className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Enter OTP sent to +91 {f.mobile}</Label>
            <Input id="su-otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="rounded-none font-mono-num tracking-[0.5em] text-lg h-11" placeholder="••••••" data-testid="su-otp" autoComplete="one-time-code" />
            <div className="flex justify-between text-xs">
              <button type="button" className="text-muted-foreground hover:text-primary underline" onClick={() => { setOtpSent(false); setOtp(""); setMockCode(""); }} data-testid="su-edit-details">Edit details</button>
              <button type="button" disabled={cooldown > 0 || busy} className="text-primary disabled:text-muted-foreground hover:underline" onClick={sendOtp} data-testid="su-resend-otp">
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
              </button>
            </div>
          </div>
        )}

        <Button type="submit" disabled={busy} className="w-full rounded-none h-11 gap-2 font-bold uppercase tracking-wide" data-testid="su-submit">
          {busy ? "Please wait…" : otpSent ? <>Verify &amp; Create Account <ArrowRight className="h-4 w-4" /></> : <>Send OTP <ArrowRight className="h-4 w-4" /></>}
        </Button>
        <p className="text-center text-sm text-muted-foreground">Already have an account? <Link to="/signin" className="text-primary font-semibold hover:underline" data-testid="su-signin-link">Sign in</Link></p>
      </form>
    </AuthShell>
  );
}

export function Field({ id, label, hint, ...props }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} className="rounded-none" data-testid={id} {...props} />
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
